import { useEffect, useState } from 'react'
import { Grid, TextField } from '@mui/material'
import { addDays } from 'date-fns'
import { DateValidationError } from '@mui/x-date-pickers'
import { SwitchContainer } from '../components'
import CheckBox from '../../../shared/UI/CheckBox'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import { getSelectValuesByType } from '../../../utils/app-utils'
import DateTimePicker from '../../../shared/UI/DateTimePickerV2'
import DatePicker from '../../../shared/UI/DatePickerV2'
import DateRangePicker from '../../../shared/UI/DateRangePickerV2'
import { isTabletDown } from '../../../theme/deviceChecks'
import { AbsenceFormProps } from './types'
import PartDayAbsenceForm from './partDayAbsenceForm'
import CovidIsoPayAlert from '../../Absence/Actions/CovidIsoPayAlert'
import Alert from '../../../shared/UI/Alert/Alert'
import { AbsenceReasonTypesEnum, AbsenceTypesEnum } from '../../Absence/types'
import { absenceFormLabels } from '../../../utils/constants'
import InfoBanner from '../../../shared/UI/InfoBanner'

function AbsenceForm(props: Readonly<AbsenceFormProps>) {
  const isSickness = props.absenceData?.absenceCategoryId === 1
  const {
    absenceData,
    handleCheckboxChange,
    validationErrors,
    absenceData: { absenceCategoryId, absenceTypeId },
  } = props

  const isReasonSelectedWedding =
    absenceCategoryId === AbsenceTypesEnum.NonSickness &&
    absenceTypeId === AbsenceReasonTypesEnum.Wedding
  const isReasonSelectedBloodDonation =
    absenceCategoryId === AbsenceTypesEnum.NonSickness &&
    absenceTypeId === AbsenceReasonTypesEnum.BloodDonation
  const [showBloodDonationAlert, setShowBloodDonationAlert] = useState(false)
  const [showWeddingAlert, setShowWeddingAlert] = useState(false)
  const isWorkBackDisabled =
    props.managerApprovedHours !== undefined && props.managerApprovedHours > 0
  const futureSicknessAbsenceWarningText = props.extendAbsenceData 
    ? absenceFormLabels.futureSicknessAbsenceExtendWarning
    : absenceFormLabels.futureSicknessAbsenceCreateWarning
  const isValidDateRange = !props.isAbsenceDateRangeError
  const showFutureSicknessAbsenceWarning = isSickness && props.hasFutureSicknessAbsence && isValidDateRange

  useEffect(() => {
    const isBloodDonationSelected =
      absenceData.absenceCategoryId === AbsenceTypesEnum.NonSickness &&
      absenceData.absenceTypeId === AbsenceReasonTypesEnum.BloodDonation
    setShowBloodDonationAlert(isBloodDonationSelected)
  }, [absenceData.absenceCategoryId, absenceData.absenceTypeId])

  useEffect(() => {
    const isWeddingSelected =
      absenceData.absenceCategoryId === AbsenceTypesEnum.NonSickness &&
      absenceData.absenceTypeId === AbsenceReasonTypesEnum.Wedding
    setShowWeddingAlert(isWeddingSelected)
  }, [absenceData.absenceCategoryId, absenceData.absenceTypeId])

  interface RenderCheckboxWithAlertProps {
    label: string
    checked?: boolean
    name: string
    errorMessage: string
    error?: boolean
  }
  const renderCheckboxWithAlert = ({
    label,
    checked,
    name,
    errorMessage,
    error,
  }: RenderCheckboxWithAlertProps) => (
    <Grid item xs={12} md={12}>
      <SwitchContainer>
        <CheckBox label={label} checked={checked} name={name} onChange={handleCheckboxChange} />
      </SwitchContainer>
      {error && <Alert severity="error" message={errorMessage} />}
    </Grid>
  )
  return (
    <>
      <Grid item xs={12}>
        <DateRangePicker
          value={props.dateRange}
          arrange={isTabletDown() ? 'Stack' : 'Horizontal'}
          spacing={2}
          maxRangeDays={2 * 365}
          required
          fromDateError={props.isAbsenceDurationError}
          toDateError={props.isAbsenceDurationError}
          onChange={props.handleDateRangeChange}
          onError={(error) => {
            if (props.onDateRangeError) {
              const hasValidationError = error !== null && (error[0] !== null || error[1] !== null)
              props.onDateRangeError(hasValidationError)
            }
          }}
          dataTestId="MyActions-Absence-"
          disabled={props.disabledAbsentFrom}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          label="Shift"
          name="shiftDescription"
          value={props.absenceData.shiftDescription}
          onChange={props.handleChange}
          disabled={props.isDisableShiftDescription}
          onBlur={props.onShiftStringBlur}
          data-testid="MyActions-Absence-shift"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <DatePicker
          label="Expected Return"
          value={props.absenceData.expectedReturn}
          required
          onChange={newValue => {
            if (!newValue) {
              return
            }
            props.setData(absence => ({ ...absence, expectedReturn: new Date(newValue) }))
          }}
          onError={(error: DateValidationError) => {
            const hasError = error !== null
            if (props.onExpectedReturnDateError) {
              props.onExpectedReturnDateError(hasError)
            }
          }}
          dataTestId="MyActions-Absence-ExpectedReturn"
        />
      </Grid>

      <PartDayAbsenceForm
        isOpen={props.absenceData.partDayAbsence}
        setIsOpen={props.setOpen}
        popperText={props.popperText}
        shiftDescription={props.absenceData.shiftDescription}
        dateRange={props.dateRange}
        absentFrom={props.absenceData.absentFrom}
        hasHadLunch={props.absenceData.hasHadLunch}
        toWorkBack={props.absenceData.toWorkBack}
        handleDateChange={props.handleDateChange}
        handleDateError={props.handleDateError}
        handleCheckboxChange={props.handleCheckboxChange}
        disabledPartDayAbsence={props.disabledPartDayAbsence}
        disabledAbsentFrom={props.disabledAbsentFrom}
        isWorkBackDisabled={isWorkBackDisabled}
        hasManAuthHistory={props.hasManAuthHistory}
      />

      <Grid item xs={12} md={6}>
        <DropdownMenu
          label="Absence Type"
          id="absenceType"
          data={props.absenceReasonTypeList}
          textField="displayValue"
          valueField="value"
          name="absenceCategoryId"
          value={props.absenceData.absenceCategoryId}
          onChange={e => {
            const filteredList = props.absenceReasonTypeList.find(
              item => `${item.value}` === `${e.target.value}`
            )
            if (filteredList) {
              props.handleChange(e)
              props.setCurrentTypeAssociatedValueId(filteredList?.value || 0)
            }
          }}
          error={props.validationErrors.absenceTypeId}
          helperText="Required"
          disabled={Boolean(props.extendAbsenceData)}
          dataTestId="MyActions-Absence-AbsenceType"
        />
      </Grid>

      {showFutureSicknessAbsenceWarning && (
        <Grid item xs={12}>
          <InfoBanner
            dataTestId="future-sickness-warning"
            text={futureSicknessAbsenceWarningText}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <DropdownMenu
          label="Reason for absence"
          id="absenceReason"
          data={props.absenceTypesList.filter(
            absenceType => absenceType.associatedValue === props.absenceData.absenceCategoryId
          )}
          textField="displayValue"
          valueField="value"
          name="absenceTypeId"
          value={props.absenceData.absenceTypeId}
          onChange={props.handleChange}
          error={props.validationErrors.absenceTypeReasonId}
          helperText="Required"
          disabled={Boolean(props.extendAbsenceData) || !props.currentTypeAssociatedValueId}
          dataTestId="MyActions-Absence-ReasonAbsence"
        />
      </Grid>
      <Grid item xs={12}>
        {showBloodDonationAlert && (
          <Alert
            severity="info"
            message="Absences due to blood donation cannot exceed two days per year."
            testId="blood-donation-alert"
          />
        )}
        {showWeddingAlert && (
          <Alert
            severity="info"
            message={absenceFormLabels.weddingNoMoreThanTwoDaysInfo}
            testId="wedding-more-than-two-days-alert"
          />
        )}
      </Grid>
      {isReasonSelectedWedding && (
        <>
          <Grid item xs={12} md={12}>
            <SwitchContainer>
              <CheckBox
                label={absenceFormLabels.weddingCheckboxLabel}
                checked={props.absenceData.weddingCertificateSubmitted}
                name="weddingCertificateSubmitted"
                onChange={props.handleCheckboxChange}
              />
            </SwitchContainer>
          </Grid>
          {validationErrors.weddingCertificateSubmitted && (
            <Grid item xs={12} md={12}>
              <Alert
                severity="error"
                message={absenceFormLabels.weddingCertificateNotSubmittedAlert}
                testId="wedding-certificate-not-submitted-alert"
              />
            </Grid>
          )}
        </>
      )}
      {isReasonSelectedBloodDonation &&
        renderCheckboxWithAlert({
          label:
            'I understand that blood donations can not total more than two days per occurrence (max. 4-5 times per year, see HR policy), and that the relevant documentation needs to be provided to HR to support this request.',
          checked: absenceData.bloodDonationConfirmed,
          name: 'bloodDonationConfirmed',
          errorMessage:
            "Please confirm that this blood donation absence doesn't exceed HR policy and you understand you need to provide relevant documentation to HR.",
          error: validationErrors.bloodDonationConfirmed,
        })}

      <CovidIsoPayAlert
        absenceCategoryId={props.absenceData.absenceCategoryId}
        absenceTypeId={props.absenceData.absenceTypeId}
        covidIsolationIndicator={props.absenceData.covidIsolationIndicator}
      />
      <Grid item xs={12} md={12}>
        <DropdownMenu
          label="Contact made by"
          id="year-select"
          data={getSelectValuesByType('ContactByType')}
          textField="displayValue"
          valueField="value"
          name="contactedByTypeId"
          value={props.absenceData.contactedByTypeId}
          onChange={props.handleChange}
          dataTestId="MyActions-Absence-NoContactMade"
        />
      </Grid>
      {(props.absenceData.contactedByTypeId === 1 || props.absenceData.contactedByTypeId === 2) && (
        <Grid item xs={12} md={12}>
          <DateTimePicker
            id="absence-contacted-date"
            label="Contacted date"
            required
            value={props.absenceData.contactDateTime}
            onChange={newValue => {
              props.handleDateChange(newValue, 'contactDateTime')
            }}
            onError={(error: string | null) => {
              const hasError = error !== null
              if (props.onContactDateTimeError) {
                props.onContactDateTimeError(hasError)
              }
            }}
            dataTestId="MyActions-Absence-ContactedDate"
          />
        </Grid>
      )}
      <Grid item xs={12} md={12}>
        <TextField
          fullWidth
          label="Contact Comments"
          rows={5}
          multiline
          value={props.absenceData.contactComments}
          onChange={props.handleChange}
          name="contactComments"
          data-testid="MyActions-Absence-ContactComments"
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <TextField
          fullWidth
          label="Comments"
          rows={5}
          multiline
          value={props.absenceData.comments}
          error={props.validationErrors.comments}
          onChange={props.handleChange}
          name="comments"
          data-testid="MyActions-Absence-Comments"
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <SwitchContainer>
          <CheckBox
            defaultChecked={props.absenceData.sendFdn}
            label="Send FDN"
            name="sendFdn"
            onChange={props.handleCheckboxChange}
            dataTestId="MyActions-Absence-SendFdn"
          />
        </SwitchContainer>
      </Grid>
    </>
  )
}

export default AbsenceForm
